import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { devtools } from "zustand/middleware";
import { ShippingService } from "../services/shipping.service";
import {
  type ShippingRule,
  type ShopConfigResponse,
  type UpdateShopLogisticsRequest,
  isPickupOfficesSupportedForShopCountry,
} from "@codway-dev/shared";

type OfficesState = ShopConfigResponse["shipping"]["offices"];

// ---------- Constants ----------

export const FREE_SHIPPING_RATE_ID = "free_shipping_default";

const FREE_SHIPPING_RATE: ShippingRule = {
  id: FREE_SHIPPING_RATE_ID,
  name: "Envío gratis",
  active: true,
  sort_order: 0,
  action: { type: "free_shipping" },
  conditions: {},
};

function ensureFreeShippingRate(rates: ShippingRule[]): ShippingRule[] {
  const hasIt = rates.some((r) => r.id === FREE_SHIPPING_RATE_ID);
  if (hasIt) return rates;
  return [{ ...FREE_SHIPPING_RATE }, ...rates];
}

// ---------- Types ----------

interface ShippingEditorState {
  rates: ShippingRule[];
  initialRates: ShippingRule[] | null;
  offices: OfficesState;
  initialOffices: OfficesState | null;
  currency: string;
  countryCode: string;
  expandedRateId: string | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
}

interface ShippingEditorActions {
  loadFromBackend: () => Promise<void>;
  addRate: () => void;
  updateRate: (id: string, updates: Partial<ShippingRule>) => void;
  removeRate: (id: string) => void;
  toggleExpanded: (id: string) => void;
  setOfficesEnabled: (enabled: boolean) => void;
  save: () => Promise<void>;
  discardChanges: () => void;
}

type ShippingEditorStore = ShippingEditorState & ShippingEditorActions;

// ---------- Selector ----------

export const selectShippingIsDirty = (state: ShippingEditorStore) => {
  if (!state.initialRates || state.initialOffices === null) return false;
  const ratesChanged =
    JSON.stringify(state.rates) !== JSON.stringify(state.initialRates);
  const officesChanged =
    JSON.stringify(state.offices) !== JSON.stringify(state.initialOffices);
  return ratesChanged || officesChanged;
};

// ---------- Store ----------

export const useShippingEditorStore = create<ShippingEditorStore>()(
  devtools(
    immer((set, get) => ({
      rates: [],
      initialRates: null,
      currency: "USD",
      countryCode: "",
      expandedRateId: null,
      isLoading: false,
      isSaving: false,
      error: null,
      offices: { enabled: false, min_score: 0 },
      initialOffices: null,

      loadFromBackend: async () => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });
        try {
          const data = await ShippingService.getLogistics();
          const rates = ensureFreeShippingRate(data.shipping.rates);
          const offices = { ...data.shipping.offices };
          if (!isPickupOfficesSupportedForShopCountry(data.country_code)) {
            offices.enabled = false;
          }
          set((state) => {
            state.rates = rates;
            state.initialRates = JSON.parse(JSON.stringify(rates));
            state.offices = offices;
            state.initialOffices = JSON.parse(JSON.stringify(offices));
            state.currency = data.currency;
            state.countryCode = data.country_code;
            state.isLoading = false;
          });
        } catch (err) {
          set((state) => {
            state.error =
              err instanceof Error ? err.message : "Failed to load settings";
            state.isLoading = false;
          });
        }
      },

      addRate: () =>
        set((state) => {
          const newId = `rate_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
          const newRate: ShippingRule = {
            id: newId,
            name: "",
            active: true,
            sort_order: state.rates.length,
            action: { type: "flat_rate", cost: 0 },
            conditions: {},
          };
          state.rates.push(newRate);
          state.expandedRateId = newId;
        }),

      updateRate: (id, updates) =>
        set((state) => {
          const index = state.rates.findIndex((r) => r.id === id);
          if (index === -1) return;
          Object.assign(state.rates[index], updates);
        }),

      removeRate: (id) =>
        set((state) => {
          if (id === FREE_SHIPPING_RATE_ID) return;
          state.rates = state.rates.filter((r) => r.id !== id);
          if (state.expandedRateId === id) {
            state.expandedRateId = null;
          }
        }),

      toggleExpanded: (id) =>
        set((state) => {
          state.expandedRateId = state.expandedRateId === id ? null : id;
        }),

      setOfficesEnabled: (enabled) =>
        set((state) => {
          if (
            enabled &&
            !isPickupOfficesSupportedForShopCountry(state.countryCode)
          ) {
            return;
          }
          state.offices.enabled = enabled;
        }),

      save: async () => {
        set((state) => {
          state.isSaving = true;
        });
        try {
          const { rates, offices, initialOffices } = get();
          const payload: UpdateShopLogisticsRequest = { rates };
          if (
            initialOffices !== null &&
            JSON.stringify(offices) !== JSON.stringify(initialOffices)
          ) {
            const pickupOk = isPickupOfficesSupportedForShopCountry(
              get().countryCode
            );
            payload.offices = {
              enabled: pickupOk && offices.enabled,
              min_score: offices.min_score,
              ...(offices.label != null && offices.label !== ""
                ? { label: offices.label }
                : {}),
            };
          }
          await ShippingService.updateLogistics(payload);
          set((state) => {
            state.isSaving = false;
            state.initialRates = JSON.parse(JSON.stringify(state.rates));
            state.initialOffices = JSON.parse(JSON.stringify(state.offices));
          });
        } catch (err) {
          set((state) => {
            state.isSaving = false;
          });
          throw err instanceof Error ? err : new Error("Failed to save");
        }
      },

      discardChanges: () =>
        set((state) => {
          if (state.initialRates) {
            state.rates = JSON.parse(JSON.stringify(state.initialRates));
            state.expandedRateId = null;
          }
          if (state.initialOffices) {
            state.offices = JSON.parse(JSON.stringify(state.initialOffices));
          }
        }),
    })),
    { name: "ShippingEditorStore" }
  )
);
